import { authService, type AuthService } from '../service/AuthService'

import { useEnv } from '@/shared/config'
import { MCP_SHELL_ORIGIN } from '@/shared/lib/mcpShell'

// A fixed window name: clicking the menu row again reuses the MCP tab instead of stacking new ones.
export const MCP_SHELL_WINDOW_NAME = 'orphex-mcp'
// Where a web user arriving from this app lands inside the shell (ORP-27467).
export const MCP_SHELL_NEXT_PATH = '/assistants'

export function isForeignFrontendOrigin(frontendOrigin?: string | null): boolean {
  if (!frontendOrigin || typeof window === 'undefined') return false
  if (
    useEnv().MODE !== 'production' &&
    ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
  )
    return false
  try {
    return new URL(frontendOrigin).origin !== window.location.origin
  } catch {
    return false
  }
}

/** The shell's `/login`, carrying the one-time `handoff` and the `next` path when there are any. */
export function buildMcpShellLoginUrl(
  shellOrigin: string,
  handoff?: string | null,
  next?: string | null,
): string {
  const params = new URLSearchParams()
  if (handoff) params.set('handoff', handoff)
  if (next) params.set('next', next)
  const query = params.toString()
  return `${shellOrigin.replace(/\/$/, '')}/login${query ? `?${query}` : ''}`
}

/**
 * Leaves the app for the MCP shell's own login page directly — no in-app interstitial route.
 * Never call anything after this; the app is being abandoned, not navigated.
 */
export function redirectToMcpShellLogin(origin: string, handoff: string): void {
  window.location.replace(buildMcpShellLoginUrl(origin || MCP_SHELL_ORIGIN, handoff))
}

let mcpShellOpening = false

/**
 * Opens mcp.orphex.co on the account selected here, without asking the user to sign in again
 * (ORP-27467). The tab is opened synchronously, inside the click, so a popup blocker lets it
 * through; it is pointed at the shell only once the one-time handoff (ORP-27464) is back.
 * Without a handoff the tab still lands on the shell's own login page.
 *
 * A click while one is still in flight is ignored.
 */
export async function openMcpShellLogin(
  accountId?: number | string | null,
  service: Pick<AuthService, 'requestShellHandoff'> = authService,
): Promise<void> {
  if (mcpShellOpening) return
  mcpShellOpening = true

  const tab = window.open('', MCP_SHELL_WINDOW_NAME)
  // The shell must not get a handle back on this app — what `noopener` used to guarantee.
  if (tab) tab.opener = null

  try {
    let url = buildMcpShellLoginUrl(MCP_SHELL_ORIGIN, null, MCP_SHELL_NEXT_PATH)
    try {
      const handoff = await service.requestShellHandoff(accountId ?? null)
      if (handoff) {
        url = buildMcpShellLoginUrl(
          handoff.frontend_origin || MCP_SHELL_ORIGIN,
          handoff.handoff,
          MCP_SHELL_NEXT_PATH,
        )
      }
    } catch {
      // Fall through to the plain login page.
    }

    if (tab && !tab.closed) tab.location.href = url
    else window.open(url, MCP_SHELL_WINDOW_NAME)
  } finally {
    mcpShellOpening = false
  }
}
